<script>
/**
 * @component AdmissionDialog
 * @description Dialog de admisión de pacientes. Muestra los turnos del día
 *              en estado 'pending' y permite admitirlos a sala de espera.
 *              Usa GET /appointments con filtros de fecha y status,
 *              y PUT /appointments/:id/admit para admitir.
 * @example <AdmissionDialog :visible.sync="admissionDialogVisible" @admitted="onAdmitted" />
 */
import appointmentService from "@/services/appointment";
import helperService from "@/services/helper";
import moment from "moment";

export default {
  name: "AdmissionDialog",

  props: {
    /** @prop {Boolean} visible - Controla visibilidad del dialog */
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      /** @type {Array} Turnos del día en estado pending */
      appointments: [],
      /** @type {boolean} Indica si está cargando los turnos */
      loading: false,
      /** @type {boolean} Indica si ya se hizo la primera carga */
      loaded: false,
      /** @type {string|null} ID del appointment siendo admitido */
      admitting: null,
    };
  },

  watch: {
    /**
     * @description Carga los turnos del día cuando el dialog se abre
     */
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchTodayAppointments();
        }
      },
    },
  },

  methods: {
    /**
     * @description Carga los turnos del día en estado pending
     */
    async fetchTodayAppointments() {
      this.loading = true;
      this.loaded = false;
      this.appointments = [];
      try {
        const startsAtFrom = moment().startOf("day").valueOf();
        const startsAtTo = moment().endOf("day").valueOf();

        const data = await appointmentService.getAppointments({
          params: {
            status: "pending",
            startsAtFrom,
            startsAtTo,
            populate: ["patient", "doctor", "medicalSpecialty"],
            limit: 200,
          },
        });

        const result = data?.docs || data;
        this.appointments = Array.isArray(result) ? result : [];
      } catch (err) {
        console.error("AdmissionDialog: error fetching appointments", err);
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },

    /**
     * @description Admite un appointment usando PUT /appointments/:id/admit
     * @param {Object} appointment - El appointment a admitir
     */
    async admit(appointment) {
      this.admitting = appointment._id;
      try {
        await appointmentService.admitAppointment(appointment._id);
        const name = helperService.getFullName(appointment.patient);
        this.$message.success(`${name} admitido/a a sala de espera`);
        this.appointments = this.appointments.filter(
          (a) => a._id !== appointment._id
        );
        this.$emit("admitted", appointment);
      } catch {
        this.$message.error("Error al admitir el paciente");
      } finally {
        this.admitting = null;
      }
    },

    /**
     * @description Nombre completo del usuario
     * @param {Object} user
     * @returns {string}
     */
    getFullName(user) {
      return helperService.getFullName(user);
    },

    /**
     * @description Hora formateada del appointment
     * @param {Object} appointment
     * @returns {string}
     */
    getTime(appointment) {
      return appointment.startsAt
        ? moment(appointment.startsAt).format("HH:mm")
        : "-";
    },

    /**
     * @description DNI del paciente
     * @param {Object} patient
     * @returns {string}
     */
    getDni(patient) {
      if (!patient) return "-";
      return patient.governmentId?.number || patient.dni || "-";
    },

    /**
     * @description Cierra el dialog y limpia el estado
     */
    reset() {
      this.appointments = [];
      this.loaded = false;
      this.admitting = null;
      this.$emit("update:visible", false);
    },
  },
};
</script>

<template lang="pug">
el-dialog(
  title="Admisión de pacientes"
  :visible="visible"
  width="660px"
  @close="reset"
  @open="fetchTodayAppointments"
)
  //- Loading
  div(v-if="loading" style="text-align: center; padding: 30px 0")
    i.el-icon-loading(style="font-size: 24px; color: #409eff")
    p(style="margin-top: 8px; color: #909399") Cargando turnos del día...

  //- Lista de turnos
  template(v-else-if="appointments.length")
    el-table(
      :data="appointments"
      border
      size="small"
    )
      el-table-column(label="Horario" width="80")
        template(slot-scope="{ row }")
          | {{ getTime(row) }}
      el-table-column(label="Paciente")
        template(slot-scope="{ row }")
          | {{ getFullName(row.patient) }}
      el-table-column(label="DNI" width="110")
        template(slot-scope="{ row }")
          | {{ getDni(row.patient) }}
      el-table-column(label="Médico" width="150")
        template(slot-scope="{ row }")
          | {{ row.doctor ? getFullName(row.doctor) : '-' }}
      el-table-column(label="" width="100")
        template(slot-scope="{ row }")
          el-button(
            size="mini"
            type="success"
            :loading="admitting === row._id"
            @click="admit(row)"
          ) Admitir

  //- Sin turnos
  el-alert(
    v-else
    title="No hay turnos pendientes para hoy"
    type="info"
    show-icon
    :closable="false"
  )
</template>

<style scoped></style>
